import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Space,
  Button,
  Typography,
  Alert,
  Spin,
  message,
  Modal,
  Tag,
  Checkbox,
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Notice } from '../../components/common/Notice';
import { surveyService } from '../../services/survey.service';
import { RichTextEditor } from '../../components/common/RichTextEditor';
import {
  Survey,
  Question,
  GeographicScope,
  SurveyStatus,
  SurveyVisibilityResponse,
  CreateSurveyData,
  UpdateSurveyData,
  UpdateSurveyTagsData,
} from '../../types/survey.types';
import {
  SurveyVisibilityLevel,
  isDateBasedVisibilityLevel,
} from '../../types/survey-visibility.types';
import { Country, profileService } from '../../services/profile.service';
import { State, City } from '../../types/profile.types';
import { parseApiError } from '../../utils/error-handler';
import { QuestionBuilderModal } from '../../components/survey/QuestionBuilderModal';
import { QuestionGroupEditor } from '../../components/survey/QuestionGroupEditor';
import { surveyQuestionGroupService } from '../../services/survey-question-group.service';
import { QuestionGroupDetail } from '../../types/survey-question-group.types';
import { SurveyLocationCard } from '../../components/survey/SurveyLocationCard';
import SurveyMinLevelField from '../../components/contractor/SurveyMinLevelField';
import SurveyTagField from '../../components/contractor/SurveyTagField';
import { validationService } from '../../services/validation.service';
import type { ValidationLevel } from '../../types/validation.types';
import type { SurveyTagOption } from '../../types/tag.types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const VISIBILITY_OPTIONS: Array<{ value: SurveyVisibilityLevel; label: string }> = [
  { value: 'PUBLIC_AFTER_CLOSE', label: 'Público, após encerramento' },
  { value: 'PUBLIC_AFTER_DATE', label: 'Público, após data a definir' },
  { value: 'PARTICIPANTS_AFTER_CLOSE', label: 'Apenas Participantes, após Encerramento' },
  { value: 'PARTICIPANTS_AFTER_DATE', label: 'Apenas Participantes, após data a definir' },
  { value: 'CONTRACTOR_ONLY', label: 'Apenas Contratante' },
];

export const ContractorSurveyEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isCreateMode = !id;
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [hasResponses, setHasResponses] = useState(false);
  const [minimumValidationLevel, setMinimumValidationLevel] = useState<ValidationLevel>('LEVEL_1');
  const [effectiveValidationLevel, setEffectiveValidationLevel] = useState<ValidationLevel>('LEVEL_1');
  const [platformFloorApplied, setPlatformFloorApplied] = useState(false);
  const [selectedVisibilityLevel, setSelectedVisibilityLevel] = useState<SurveyVisibilityLevel>('PUBLIC_AFTER_CLOSE');
  const [availableTags, setAvailableTags] = useState<SurveyTagOption[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [questionGroups, setQuestionGroups] = useState<QuestionGroupDetail[]>([]);

  // Geographic scope state
  const [geographicScope, setGeographicScope] = useState<GeographicScope>(GeographicScope.NATIONAL);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [countryInputValue, setCountryInputValue] = useState('');
  const [stateInputValue, setStateInputValue] = useState('');
  const [cityInputValue, setCityInputValue] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [countrySearchText, setCountrySearchText] = useState('');
  const [stateSearchText, setStateSearchText] = useState('');
  const [citySearchText, setCitySearchText] = useState('');
  const [searchingCountries, setSearchingCountries] = useState(false);
  const [searchingStates, setSearchingStates] = useState(false);
  const [searchingCities, setSearchingCities] = useState(false);
  const [brazilCountryId, setBrazilCountryId] = useState('');

  useEffect(() => {
    if (isCreateMode) {
      setLoading(false);
      setError(null);
      setSurvey(null);
      setQuestions([]);
      setHasResponses(false);
      setGeographicScope(GeographicScope.NATIONAL);
      setSelectedCountry(null);
      setSelectedState(null);
      setSelectedCity(null);
      setCountryInputValue('');
      setStateInputValue('');
      setCityInputValue('');
      form.setFieldsValue({
        geographicScope: GeographicScope.NATIONAL,
        surveyValue: 0,
        status: SurveyStatus.DRAFT,
        visibilityLevel: 'PUBLIC_AFTER_CLOSE',
      });
      return;
    }

    loadSurvey();
    loadQuestionGroups();
  }, [id, isCreateMode, form]);

  const loadQuestionGroups = async () => {
    if (!id || isCreateMode) return;
    try {
      const items = await surveyQuestionGroupService.listGroupsContractor(id);
      setQuestionGroups(items);
    } catch {
      // groups are optional — silently ignore
    }
  };

  // Reload groups every time the question modal opens so new groups are visible
  useEffect(() => {
    if (showQuestionModal) {
      loadQuestionGroups();
    }
  }, [showQuestionModal]);

  // Auto-load Brazil for national scope
  useEffect(() => {
    const findBrazil = async () => {
      try {
        const result = await profileService.getCountries('brasil');
        if (result.length > 0) {
          setBrazilCountryId(result[0].id);
        }
      } catch {
        // ignore
      }
    };
    findBrazil();
  }, []);

  // Country search debounce
  useEffect(() => {
    if (geographicScope !== GeographicScope.NATIONAL) return;
    if (countrySearchText.length < 2 && countrySearchText.length > 0) return;
    const timer = setTimeout(() => searchCountries(countrySearchText), 300);
    return () => clearTimeout(timer);
  }, [countrySearchText, geographicScope]);

  // State search debounce
  useEffect(() => {
    if (geographicScope !== GeographicScope.STATE || !selectedCountry) return;
    if (stateSearchText.length < 2 && stateSearchText.length > 0) return;
    const timer = setTimeout(() => searchStates(selectedCountry.id, stateSearchText), 300);
    return () => clearTimeout(timer);
  }, [stateSearchText, selectedCountry, geographicScope]);

  // City search debounce
  useEffect(() => {
    if (geographicScope !== GeographicScope.CITY || !selectedState) return;
    if (citySearchText.length < 2 && citySearchText.length > 0) return;
    const timer = setTimeout(() => searchCities(selectedState.id, citySearchText), 300);
    return () => clearTimeout(timer);
  }, [citySearchText, selectedState, geographicScope]);

  useEffect(() => {
    if (isCreateMode) {
      loadActiveTagsForCreate();
    }
  }, [isCreateMode]);

  const searchCountries = async (search: string) => {
    try {
      setSearchingCountries(true);
      setCountries(await profileService.getCountries(search));
    } catch {
      // ignore
    } finally {
      setSearchingCountries(false);
    }
  };

  const searchStates = async (countryId: string, search: string) => {
    try {
      setSearchingStates(true);
      setStates(await profileService.getStatesByCountry(countryId, search));
    } catch {
      // ignore
    } finally {
      setSearchingStates(false);
    }
  };

  const searchCities = async (stateId: string, search: string) => {
    try {
      setSearchingCities(true);
      setCities(await profileService.getCitiesByState(stateId, search));
    } catch {
      // ignore
    } finally {
      setSearchingCities(false);
    }
  };

  const loadSurvey = async () => {
    try {
      setLoading(true);
      setError(null);

      const surveyData = await surveyService.getSurvey(id!);
      const activeTags = await surveyService.getActiveSurveyTags();
      const visibilityData: SurveyVisibilityResponse = await surveyService.getSurveyVisibility(id!);
      setSurvey(surveyData);
      setAvailableTags(activeTags);
      setSelectedTagIds((surveyData.tags || []).map((tag) => tag.id));
      setSelectedVisibilityLevel(visibilityData.visibilityLevel);

      const surveyHasResponses =
        (surveyData._count?.responses || 0) > 0 ||
        surveyData.status === SurveyStatus.ACTIVE ||
        surveyData.status === SurveyStatus.COMPLETED;
      setHasResponses(surveyHasResponses);

      const questionsData = await surveyService.getQuestions(id!);
      setQuestions(questionsData);

      try {
        const policy = await validationService.getSurveyValidationPolicy(id!);
        setMinimumValidationLevel(policy.minimumValidationLevel);
        setEffectiveValidationLevel(policy.effectiveLevel);
        setPlatformFloorApplied(policy.platformFloorApplied);
      } catch {
        setMinimumValidationLevel('LEVEL_1');
        setEffectiveValidationLevel('LEVEL_1');
        setPlatformFloorApplied(false);
      }

      const scope = surveyData.geographicScope;
      setGeographicScope(scope);

      form.setFieldsValue({
        name: surveyData.name,
        description: surveyData.description,
        geographicScope: scope,
        surveyValue: surveyData.surveyValue,
        dateRange:
          surveyData.startDate && surveyData.endDate
            ? [dayjs(surveyData.startDate), dayjs(surveyData.endDate)]
            : undefined,
        status: surveyData.status,
        isElectoral: Boolean(surveyData.isElectoral),
        visibilityLevel: visibilityData.visibilityLevel,
        disclosureDate: visibilityData.disclosureDate ? dayjs(visibilityData.disclosureDate) : undefined,
      });

      if ((surveyData as any).country) {
        const country = (surveyData as any).country as Country;
        setSelectedCountry(country);
        setCountryInputValue(country.name);
      }
      // Only hydrate state/city when the saved scope actually requires them. A survey
      // saved with a stray stateId/cityId (e.g. scope was later changed to NATIONAL
      // without clearing them) must not be shown as if it still had that location set,
      // otherwise the disabled input is stuck displaying a value the user can't remove.
      if (
        (scope === GeographicScope.STATE || scope === GeographicScope.CITY) &&
        (surveyData as any).state
      ) {
        const state = (surveyData as any).state as State;
        setSelectedState(state);
        setStateInputValue(`${state.name} (${state.code})`);
      }
      if (scope === GeographicScope.CITY && (surveyData as any).city) {
        const city = (surveyData as any).city as City;
        setSelectedCity(city);
        setCityInputValue(city.name);
      }
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTagsForCreate = async () => {
    try {
      const activeTags = await surveyService.getActiveSurveyTags();
      setAvailableTags(activeTags);
    } catch {
      // ignore - form remains usable without tag suggestions
    }
  };

  const handleScopeChange = (value: GeographicScope) => {
    setGeographicScope(value);
    // Clear dependent state
    setSelectedState(null);
    setSelectedCity(null);
    setStateInputValue('');
    setCityInputValue('');
    setStates([]);
    setCities([]);

    if (value === GeographicScope.NATIONAL) {
      // Auto-fill Brazil if available
      if (brazilCountryId && !selectedCountry) {
        // Country already set from loaded survey; if not, clear
      }
    } else if (value === GeographicScope.STATE || value === GeographicScope.CITY) {
      // Keep country but clear state/city (done above)
    }
  };

  const handleVisibilityLevelChange = (value: SurveyVisibilityLevel) => {
    setSelectedVisibilityLevel(value);
    if (!isDateBasedVisibilityLevel(value)) {
      form.setFieldValue('disclosureDate', undefined);
    }
  };

  const handleCountrySearch = (value: string) => {
    setCountrySearchText(value);
    setCountryInputValue(value);
  };

  const handleCountrySelect = (value: string) => {
    const country = [...countries, ...(selectedCountry ? [selectedCountry] : [])].find(
      (c) => c.id === value
    );
    if (country) {
      setSelectedCountry(country);
      setCountryInputValue(country.name);
      setSelectedState(null);
      setSelectedCity(null);
      setStateInputValue('');
      setCityInputValue('');
    }
  };

  const handleStateSearch = (value: string) => {
    setStateSearchText(value);
    setStateInputValue(value);
  };

  const handleStateSelect = (value: string) => {
    const state = [...states, ...(selectedState ? [selectedState] : [])].find(
      (s) => s.id === value
    );
    if (state) {
      setSelectedState(state);
      setStateInputValue(`${state.name} (${state.code})`);
      setSelectedCity(null);
      setCityInputValue('');
    }
  };

  const handleCitySearch = (value: string) => {
    setCitySearchText(value);
    setCityInputValue(value);
  };

  const handleCitySelect = (value: string) => {
    const city = [...cities, ...(selectedCity ? [selectedCity] : [])].find(
      (c) => c.id === value
    );
    if (city) {
      setSelectedCity(city);
      setCityInputValue(city.name);
    }
  };

  const canEditAllSurveyFields =
    isCreateMode || (!!survey && survey.status === SurveyStatus.DRAFT && !hasResponses);

  const handleSave = async (values: any) => {
    try {
      setSaving(true);

      if (!values.dateRange || !values.dateRange[0] || !values.dateRange[1]) {
        message.error('Período de coleta é obrigatório');
        return;
      }

      if (isCreateMode) {
        const createData: CreateSurveyData = {
          name: values.name,
          description: values.description,
          geographicScope: values.geographicScope,
          // Contractor cannot define survey value; admin manages this field.
          surveyValue: 0,
          startDate: values.dateRange[0].toISOString(),
          endDate: values.dateRange[1].toISOString(),
          visibilityLevel: values.visibilityLevel,
          disclosureDate: isDateBasedVisibilityLevel(values.visibilityLevel)
            ? values.disclosureDate?.format('YYYY-MM-DD')
            : null,
          isElectoral: Boolean(values.isElectoral),
        };

        if (values.geographicScope === GeographicScope.NATIONAL && selectedCountry) {
          createData.countryId = selectedCountry.id;
        } else if (values.geographicScope === GeographicScope.STATE && selectedState) {
          createData.countryId = selectedCountry?.id;
          createData.stateId = selectedState.id;
        } else if (values.geographicScope === GeographicScope.CITY && selectedCity) {
          createData.countryId = selectedCountry?.id;
          createData.stateId = selectedState?.id;
          createData.cityId = selectedCity.id;
        }

        const createdSurvey = await surveyService.createSurvey(createData);

        const createTagPayload: UpdateSurveyTagsData = { tagIds: selectedTagIds };
        if (createTagPayload.tagIds.length > 0) {
          await surveyService.updateContractorSurveyTags(createdSurvey.id, createTagPayload);
        }

        message.success('Pesquisa criada com status Rascunho. Agora adicione as questões.');
        navigate(`/contractor/surveys/${createdSurvey.id}/edit`);
        return;
      }

      const updateData: UpdateSurveyData = {
        description: values.description,
        visibilityLevel: values.visibilityLevel,
        disclosureDate: isDateBasedVisibilityLevel(values.visibilityLevel)
          ? values.disclosureDate?.format('YYYY-MM-DD')
          : null,
      };

      updateData.startDate = values.dateRange[0].toISOString();
      updateData.endDate = values.dateRange[1].toISOString();

      if (canEditAllSurveyFields) {
        updateData.name = values.name;
        updateData.geographicScope = values.geographicScope;
        updateData.isElectoral = Boolean(values.isElectoral);

        if (values.geographicScope === GeographicScope.NATIONAL && selectedCountry) {
          updateData.countryId = selectedCountry.id;
        } else if (values.geographicScope === GeographicScope.STATE && selectedState) {
          updateData.countryId = selectedCountry?.id;
          updateData.stateId = selectedState.id;
        } else if (values.geographicScope === GeographicScope.CITY && selectedCity) {
          updateData.countryId = selectedCountry?.id;
          updateData.stateId = selectedState?.id;
          updateData.cityId = selectedCity.id;
        }
      }

      await surveyService.updateSurvey(id!, updateData);

      const updateTagPayload: UpdateSurveyTagsData = { tagIds: selectedTagIds };
      await surveyService.updateContractorSurveyTags(id!, updateTagPayload);

      await validationService.upsertSurveyValidationPolicy(id!, {
        minimumValidationLevel,
      });

      message.success('Pesquisa atualizada com sucesso!');
      navigate('/contractor/surveys');
    } catch (err) {
      message.error(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    Modal.confirm({
      title: 'Excluir Questão',
      content: 'Tem certeza que deseja excluir esta questão?',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await surveyService.deleteQuestion(id!, questionId);
          message.success('Questão excluída com sucesso!');
          loadSurvey();
        } catch (err) {
          message.error(parseApiError(err).message);
        }
      },
    });
  };

  const canEditOnlyDescriptionAndPeriod = !isCreateMode && !canEditAllSurveyFields;
  const canEditQuestions = () => !isCreateMode && canEditAllSurveyFields;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Carregando pesquisa..." />
      </div>
    );
  }

  if (!isCreateMode && (error || !survey)) {
    return (
      <div style={{ padding: '24px' }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/contractor/surveys')}
        >
          Voltar
        </Button>
        <Alert
          message="Erro"
          description={error || 'Pesquisa não encontrada'}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  const countryOptions = [
    ...(selectedCountry
      ? [{ value: selectedCountry.id, label: `${selectedCountry.name} (${selectedCountry.code})` }]
      : []),
    ...countries
      .filter((c) => !selectedCountry || c.id !== selectedCountry.id)
      .map((c) => ({ value: c.id, label: `${c.name} (${c.code})` })),
  ];

  const stateOptions = [
    ...(selectedState
      ? [{ value: selectedState.id, label: `${selectedState.name} (${selectedState.code})` }]
      : []),
    ...states
      .filter((s) => !selectedState || s.id !== selectedState.id)
      .map((s) => ({ value: s.id, label: `${s.name} (${s.code})` })),
  ];

  const cityOptions = [
    ...(selectedCity ? [{ value: selectedCity.id, label: selectedCity.name }] : []),
    ...cities
      .filter((c) => !selectedCity || c.id !== selectedCity.id)
      .map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/contractor/surveys')}
            style={{ paddingLeft: 0 }}
          >
            Voltar para Minhas Pesquisas
          </Button>
          <Title level={2}>{isCreateMode ? 'Nova Pesquisa' : 'Editar Pesquisa'}</Title>
          <Text type="secondary">
            {isCreateMode
              ? 'A pesquisa será criada automaticamente com status Rascunho.'
              : 'Configure as informações da pesquisa. O campo contratante não pode ser alterado.'}
          </Text>
        </div>

        {hasResponses && (
          <Notice
            message="Pesquisa com Respostas"
            description="Esta pesquisa já possui respostas registradas. As questões não podem ser editadas."
            type="warning"
            showIcon
          />
        )}

        {canEditOnlyDescriptionAndPeriod && (
          <Notice
            message="Edição limitada para contratante"
            description="Nesta fase, você pode ajustar apenas descrição e período de coleta. Somente administrador pode ativar pesquisa."
            type="info"
            showIcon
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Card title="Informações Básicas">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Contratante">
                  <Input
                    value={survey?.contractor?.tradeName || 'Vinculado ao seu cadastro de contratante'}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', color: '#000' }}
                  />
                </Form.Item>
                <Notice
                  message="O contratante não pode ser alterado"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              </Col>

              <Col span={24}>
                <Form.Item
                  label="Nome da Pesquisa"
                  name="name"
                  rules={[{ required: true, message: 'Nome é obrigatório' }]}
                >
                  <Input
                    placeholder="Ex: Pesquisa de Satisfação 2024"
                    disabled={!canEditAllSurveyFields}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="Descrição"
                  name="description"
                  rules={[{ required: true, message: 'Descrição é obrigatória' }]}
                >
                  <RichTextEditor placeholder="Descreva o objetivo e contexto da pesquisa..." />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Período de Coleta"
                  name="dateRange"
                  rules={[{ required: true, message: 'Período é obrigatório' }]}
                >
                  <RangePicker
                    showTime={{ format: 'HH:mm' }}
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY HH:mm"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Valor da Pesquisa (R$)"
                  name="surveyValue"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    precision={2}
                    disabled
                    formatter={(value) =>
                      `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                  />
                </Form.Item>
                <div style={{ color: '#999', fontSize: 12, marginTop: -16 }}>
                  Somente administradores podem alterar o valor da pesquisa.
                </div>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Abrangência Geográfica"
                  name="geographicScope"
                  rules={[{ required: true, message: 'Abrangência é obrigatória' }]}
                >
                  <Select
                    placeholder="Selecione a abrangência"
                    onChange={handleScopeChange}
                    disabled={!canEditAllSurveyFields}
                  >
                    <Select.Option value={GeographicScope.NATIONAL}>Nacional</Select.Option>
                    <Select.Option value={GeographicScope.STATE}>Estadual</Select.Option>
                    <Select.Option value={GeographicScope.CITY}>Municipal</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="isElectoral" valuePropName="checked" style={{ marginTop: 30 }}>
                  <Checkbox disabled={!canEditAllSurveyFields}>Pesquisa eleitoral</Checkbox>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[{ required: true, message: 'Status é obrigatório' }]}
                >
                  <Select
                    placeholder="Selecione o status"
                    disabled
                  >
                    <Select.Option value={SurveyStatus.DRAFT}>Rascunho</Select.Option>
                    <Select.Option value={SurveyStatus.SCHEDULED}>Agendada</Select.Option>
                    <Select.Option value={SurveyStatus.ACTIVE} disabled>
                      Ativa (somente administrador)
                    </Select.Option>
                    <Select.Option value={SurveyStatus.PAUSED}>Pausada</Select.Option>
                    <Select.Option value={SurveyStatus.COMPLETED}>Concluída</Select.Option>
                    <Select.Option value={SurveyStatus.CANCELED}>Cancelada</Select.Option>
                  </Select>
                </Form.Item>
                <div style={{ color: '#999', fontSize: 12, marginTop: -16 }}>
                  Somente administradores podem alterar o status da pesquisa.
                </div>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Nível de Visibilidade dos Resultados"
                  name="visibilityLevel"
                  rules={[{ required: true, message: 'Nível de visibilidade é obrigatório' }]}
                >
                  <Select
                    placeholder="Selecione o nível de visibilidade"
                    onChange={handleVisibilityLevelChange}
                  >
                    {VISIBILITY_OPTIONS.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <SurveyTagField
                  availableTags={availableTags}
                  selectedTagIds={selectedTagIds}
                  linkedTags={survey?.tags || []}
                  onChange={setSelectedTagIds}
                />
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Data da Divulgação"
                  name="disclosureDate"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!isDateBasedVisibilityLevel(selectedVisibilityLevel)) {
                          return Promise.resolve();
                        }
                        if (!value) {
                          return Promise.reject(new Error('Data da divulgação é obrigatória para este nível'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabled={!isDateBasedVisibilityLevel(selectedVisibilityLevel)}
                  />
                </Form.Item>
                <div style={{ color: '#999', fontSize: 12, marginTop: -16 }}>
                  Campo usado em "Público, após data a definir" e "Apenas Participantes, após data a definir".
                </div>
              </Col>

              {!isCreateMode && (
                <Col span={24}>
                  <SurveyMinLevelField
                    value={minimumValidationLevel}
                    effectiveLevel={effectiveValidationLevel}
                    platformFloorApplied={platformFloorApplied}
                    disabled={!canEditAllSurveyFields}
                    onChange={setMinimumValidationLevel}
                  />
                </Col>
              )}
            </Row>
          </Card>

          <SurveyLocationCard
            geographicScope={geographicScope}
            countryInputValue={countryInputValue}
            stateInputValue={stateInputValue}
            cityInputValue={cityInputValue}
            countryOptions={countryOptions}
            stateOptions={stateOptions}
            cityOptions={cityOptions}
            countryDisabled={!canEditAllSurveyFields || geographicScope !== GeographicScope.NATIONAL}
            stateDisabled={
              !canEditAllSurveyFields ||
              (geographicScope !== GeographicScope.STATE && geographicScope !== GeographicScope.CITY)
            }
            cityDisabled={!canEditAllSurveyFields || geographicScope !== GeographicScope.CITY || !selectedState}
            searchingCountries={searchingCountries}
            searchingStates={searchingStates}
            searchingCities={searchingCities}
            onCountrySearch={handleCountrySearch}
            onCountrySelect={handleCountrySelect}
            onStateSearch={handleStateSearch}
            onStateSelect={handleStateSelect}
            onCitySearch={handleCitySearch}
            onCitySelect={handleCitySelect}
            countryHelperText={
              geographicScope === GeographicScope.NATIONAL
                ? 'Digite pelo menos 2 caracteres para buscar'
                : 'Apenas para abrangência Nacional'
            }
            stateHelperText={
              geographicScope === GeographicScope.NATIONAL
                ? 'Apenas para abrangência Estadual/Municipal'
                : 'Digite pelo menos 2 caracteres para buscar'
            }
            cityHelperText={
              geographicScope !== GeographicScope.CITY
                ? 'Apenas para abrangência Municipal'
                : !selectedState
                ? 'Selecione um estado primeiro'
                : 'Digite pelo menos 2 caracteres para buscar'
            }
          />

          {isCreateMode ? (
            <Card title="Questões" style={{ marginTop: 16 }}>
              <Notice
                message="Salve a pesquisa para começar a cadastrar questões"
                type="info"
                showIcon
              />
            </Card>
          ) : (
            <Card
              title="Questões"
              style={{ marginTop: 16 }}
              extra={
                canEditQuestions() && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingQuestion(null);
                      setShowQuestionModal(true);
                    }}
                  >
                    Adicionar Questão
                  </Button>
                )
              }
            >
              {!canEditQuestions() && (
                <Notice
                  message="As questões só podem ser editadas em pesquisas rascunho sem respostas."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {questions.length === 0 ? (
                  <Notice message="Nenhuma questão adicionada" type="info" />
                ) : (
                  questions.map((question, index) => (
                    <Card
                      key={question.id}
                      size="small"
                      title={`${index + 1}. ${question.shortDescription}`}
                      extra={
                        canEditQuestions() && (
                          <Space>
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setEditingQuestion(question);
                                setShowQuestionModal(true);
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteQuestion(question.id)}
                            >
                              Excluir
                            </Button>
                          </Space>
                        )
                      }
                    >
                      {question.longDescription && (
                        <Text type="secondary">{question.longDescription}</Text>
                      )}
                      <div style={{ marginTop: 8 }}>
                        <Tag>{question.questionType}</Tag>
                        {question.required && <Tag color="red">Obrigatória</Tag>}
                        {(() => {
                          const group = questionGroups.find(
                            (g) => String(g.id) === String((question as any).questionGroupId),
                          );
                          if (!group) return null;
                          return (
                            <Tag color={group.scope === 'NATIONAL' ? 'blue' : 'purple'}>
                              {group.name}
                              {group.scope === 'STATE' && group.stateName ? ` — ${group.stateName}` : ''}
                            </Tag>
                          );
                        })()}
                      </div>
                    </Card>
                  ))
                )}
              </Space>
            </Card>
          )}

          {!isCreateMode && id && (
            <Card title="Grupos de Perguntas" style={{ marginTop: 16 }}>
              <QuestionGroupEditor
                surveyId={id}
                surveyScope={geographicScope}
                isAdmin={false}
              />
            </Card>
          )}

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => navigate('/contractor/surveys')}>Cancelar</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {isCreateMode ? 'Criar Pesquisa' : 'Salvar Alterações'}
              </Button>
            </Space>
          </div>
        </Form>
      </Space>

      {id && (
        <QuestionBuilderModal
          surveyId={id}
          question={editingQuestion}
          nextOrder={questions.length + 1}
          open={showQuestionModal}
          availableGroups={questionGroups}
          surveyIsElectoral={Boolean(survey?.isElectoral)}
          onSave={() => {
            setShowQuestionModal(false);
            loadSurvey();
          }}
          onCancel={() => setShowQuestionModal(false)}
        />
      )}
    </div>
  );
};
